import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/AssetDetail.jsx. `department` is
// rendered via labelFor() here (list-item-backed, same as `category`) —
// the source displayed the raw stored value for department instead of its
// list label, which this port treats as a minor oversight to fix rather
// than replicate.
interface AssetRecord {
  id: number;
  description: string;
  quantity: number | null;
  asset_type: string;
  category: string | null;
  department: string | null;
  level: string | null;
  location: string | null;
  asset_tag: string | null;
  serial_number: string | null;
  purchase_value: number | null;
  purchase_date: string | null;
  warranty: string | null;
  vendor: string | null;
  brand: string | null;
  model_number: string | null;
  notes: string | null;
  useful_life_years: number | null;
  residual_value: number | null;
  custodian: { user: { full_name: string } } | null;
  disposed_at: string | null;
  disposal_proceeds: number | null;
  disposal_reason: string | null;
  impaired_at: string | null;
  impairment_loss_amount: number | null;
  impairment_reason: string | null;
  revalued_at: string | null;
  revalued_amount: number | null;
  revaluation_surplus_balance: number | null;
  revaluations: AssetRevaluationRecord[];
  status: 'active' | 'pending_disposal' | 'disposed' | 'impaired';
  disposal_requests: AssetDisposalRequestRecord[];
  documents: AssetDocumentRecord[];
}

interface AssetRevaluationRecord {
  id: number;
  revalued_at: string;
  previous_carrying_value: number;
  fair_value: number;
  surplus_movement: number;
  pl_movement: number;
  reason: string;
  created_by: { full_name: string } | null;
}

interface AssetDisposalRequestRecord {
  id: number;
  disposed_at: string;
  disposal_proceeds: number;
  disposal_reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by: { id: number; full_name: string };
  decided_by: { id: number; full_name: string } | null;
  decision_comment: string | null;
}

interface AssetDocumentRecord {
  id: number;
  category: string;
  original_name: string;
  notes: string | null;
}

const STATUS_LABEL: Record<AssetRecord['status'], string> = {
  active: 'Active',
  pending_disposal: 'Pending Disposal',
  disposed: 'Disposed',
  impaired: 'Impaired',
};
const STATUS_BADGE: Record<AssetRecord['status'], string> = {
  active: 'badge-active',
  pending_disposal: 'badge-scheduled',
  disposed: 'badge-urgent',
  impaired: 'badge-normal',
};

type FormErrors = Record<string, string[]>;

export function AssetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists, settings } = useConfig();
  const { can, user } = useAuth();
  const confirmDelete = useConfirm();
  const { showSuccess, showError } = useToast();
  const [asset, setAsset] = useState<AssetRecord | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  const [requestingDisposal, setRequestingDisposal] = useState(false);
  const [disposalRequestForm, setDisposalRequestForm] = useState({
    disposed_at: '',
    disposal_proceeds: '',
    disposal_reason: '',
  });

  const [rejectingRequest, setRejectingRequest] = useState<AssetDisposalRequestRecord | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  const [uploadCategory, setUploadCategory] = useState('');

  const [markingImpaired, setMarkingImpaired] = useState(false);
  const [impairForm, setImpairForm] = useState({ impaired_at: '', impairment_loss_amount: '', impairment_reason: '' });

  const [revaluing, setRevaluing] = useState(false);
  const [revalueForm, setRevalueForm] = useState({ revalued_at: '', fair_value: '', reason: '' });
  const [revaluePreview, setRevaluePreview] = useState<AssetRevaluationRecord | null>(null);

  const load = () => {
    api.get<AssetRecord>(`/assets/${id}`).then((res) => setAsset(res.data));
  };

  useEffect(load, [id]);

  const remove = async () => {
    if (!asset) return;
    if (!(await confirmDelete(`Delete "${asset.description}"?`))) return;
    try {
      await api.delete(`/assets/${id}`);
      navigate('/resources/assets');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openRequestDisposal = () => {
    setDisposalRequestForm({ disposed_at: new Date().toISOString().slice(0, 10), disposal_proceeds: '', disposal_reason: '' });
    setErrors({});
    setRequestingDisposal(true);
  };

  const submitDisposalRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    try {
      await api.post(`/assets/${asset.id}/disposal-requests`, disposalRequestForm);
      setRequestingDisposal(false);
      showSuccess('Disposal requested — the approver has been notified by email.');
      load();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const approveDisposal = async (disposalRequest: AssetDisposalRequestRecord) => {
    if (
      !(await confirmDelete('Approve this disposal? The asset will be marked disposed immediately.', {
        confirmLabel: 'Approve',
      }))
    ) {
      return;
    }
    try {
      const res = await api.post<{ gain_loss: number }>(`/asset-disposal-requests/${disposalRequest.id}/decide`, {
        decision: 'approved',
      });
      showSuccess(
        `Disposal approved — ${res.data.gain_loss >= 0 ? 'gain' : 'loss'} of ${formatMoney(Math.abs(res.data.gain_loss), settings.currency)}.`
      );
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to approve.');
    }
  };

  const openRejectDisposal = (disposalRequest: AssetDisposalRequestRecord) => {
    setRejectComment('');
    setErrors({});
    setRejectingRequest(disposalRequest);
  };

  const submitRejectDisposal = async (e: FormEvent) => {
    e.preventDefault();
    if (!rejectingRequest) return;
    try {
      await api.post(`/asset-disposal-requests/${rejectingRequest.id}/decide`, {
        decision: 'rejected',
        decision_comment: rejectComment,
      });
      setRejectingRequest(null);
      showSuccess('Disposal request rejected.');
      load();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const uploadDocument = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!asset) return;
    const form = e.currentTarget;
    const fileInput = form.elements.namedItem('file') as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file || !uploadCategory) return;

    const body = new FormData();
    body.append('category', uploadCategory);
    body.append('file', file);
    try {
      await api.post(`/assets/${asset.id}/documents`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadCategory('');
      form.reset();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const removeDocument = async (doc: AssetDocumentRecord) => {
    if (!(await confirmDelete(`Delete "${doc.original_name}"?`))) return;
    try {
      await api.delete(`/asset-documents/${doc.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openMarkImpaired = () => {
    setImpairForm({ impaired_at: new Date().toISOString().slice(0, 10), impairment_loss_amount: '', impairment_reason: '' });
    setErrors({});
    setMarkingImpaired(true);
  };

  const submitMarkImpaired = async (e: FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    try {
      await api.post(`/assets/${asset.id}/impair`, impairForm);
      setMarkingImpaired(false);
      showSuccess('Asset marked impaired.');
      load();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const openRevalue = () => {
    setRevalueForm({ revalued_at: new Date().toISOString().slice(0, 10), fair_value: '', reason: '' });
    setRevaluePreview(null);
    setErrors({});
    setRevaluing(true);
  };

  const submitRevalue = async (e: FormEvent) => {
    e.preventDefault();
    if (!asset) return;
    try {
      const res = await api.post<{ revaluation: AssetRevaluationRecord }>(`/assets/${asset.id}/revalue`, revalueForm);
      setRevaluePreview(res.data.revaluation);
      showSuccess('Asset revalued.');
      load();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  if (!asset) return null;

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const pendingDisposal = asset.disposal_requests.find((r) => r.status === 'pending') ?? null;
  const canRequestDisposal =
    can('resources.assets.dispose') && asset.asset_type === 'fixed' && asset.status === 'active';
  const canDecideDisposal = can('resources.assets.dispose') && !!pendingDisposal && pendingDisposal.requested_by.id !== user?.id;
  const canMarkImpaired = can('resources.update') && asset.asset_type === 'fixed' && !asset.disposed_at;
  const canRevalue = can('resources.update') && asset.asset_type === 'fixed' && !asset.disposed_at;
  const canUploadDocument = can('resources.create');
  const canDeleteDocument = can('resources.archive');
  const isWrittenOff = asset.disposed_at && (asset.disposal_proceeds ?? 0) === 0;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/resources/assets')} />
          <IconButton icon={IconEdit} label="Edit" to={`/resources/assets/${id}/edit`} />
          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />
        </div>
        {(canRequestDisposal || canMarkImpaired || canRevalue) && (
          <div className="page-header-actions">
            {canRevalue && (
              <button type="button" className="btn btn-ghost" onClick={openRevalue}>
                Revalue
              </button>
            )}
            {canMarkImpaired && (
              <button type="button" className="btn btn-ghost" onClick={openMarkImpaired}>
                Mark Impaired
              </button>
            )}
            {canRequestDisposal && (
              <button type="button" className="btn btn-danger-ghost" onClick={openRequestDisposal}>
                Request Disposal
              </button>
            )}
          </div>
        )}
      </header>

      {pendingDisposal && (
        <div className="card alert alert-warning">
          <p>
            <strong>Disposal requested</strong> by {pendingDisposal.requested_by.full_name} for{' '}
            {pendingDisposal.disposed_at} — proceeds {fmtMoney(pendingDisposal.disposal_proceeds)}.
          </p>
          <p className="muted">Reason: {pendingDisposal.disposal_reason}</p>
          {canDecideDisposal ? (
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => openRejectDisposal(pendingDisposal)}>
                Reject
              </button>
              <button type="button" className="btn btn-primary" onClick={() => approveDisposal(pendingDisposal)}>
                Approve Disposal
              </button>
            </div>
          ) : (
            can('resources.assets.dispose') && (
              <p className="muted">A different Resources Administrator must decide this request.</p>
            )
          )}
        </div>
      )}

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{asset.description}</div>
            <span className={`badge badge-${asset.asset_type === 'fixed' ? 'important' : 'normal'}`}>
              {asset.asset_type === 'fixed' ? 'Fixed' : 'Non-Fixed'}
            </span>
            <span className={`badge ${STATUS_BADGE[asset.status]}`}>{STATUS_LABEL[asset.status]}</span>
            {isWrittenOff && <span className="badge badge-urgent">Written Off</span>}
            {asset.revalued_at && <span className="badge badge-normal">Revalued</span>}
            <div className="detail-summary-service">
              {asset.category ? labelFor(lists, 'asset_category', asset.category) : 'No category'}
            </div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Quantity</span>
            <span className="detail-item-value">{asset.quantity ?? 1}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Asset tag</span>
            <span className={`detail-item-value${asset.asset_tag ? '' : ' empty'}`}>
              {asset.asset_tag || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Serial number</span>
            <span className={`detail-item-value${asset.serial_number ? '' : ' empty'}`}>
              {asset.serial_number || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Location</span>
            <span className={`detail-item-value${asset.location ? '' : ' empty'}`}>{asset.location || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Department</span>
            <span className={`detail-item-value${asset.department ? '' : ' empty'}`}>
              {asset.department ? labelFor(lists, 'asset_department', asset.department) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Level</span>
            <span className={`detail-item-value${asset.level ? '' : ' empty'}`}>{asset.level || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Vendor</span>
            <span className={`detail-item-value${asset.vendor ? '' : ' empty'}`}>{asset.vendor || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Brand / Model</span>
            <span className={`detail-item-value${asset.brand || asset.model_number ? '' : ' empty'}`}>
              {[asset.brand, asset.model_number].filter(Boolean).join(' / ') || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Purchase value</span>
            <span className={`detail-item-value${asset.purchase_value ? '' : ' empty'}`}>
              {asset.purchase_value ? fmtMoney(asset.purchase_value) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Purchase date</span>
            <span className={`detail-item-value${asset.purchase_date ? '' : ' empty'}`}>
              {asset.purchase_date || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Warranty</span>
            <span className={`detail-item-value${asset.warranty ? '' : ' empty'}`}>{asset.warranty || 'Not set'}</span>
          </div>
          {asset.asset_type === 'fixed' && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Useful life</span>
                <span className={`detail-item-value${asset.useful_life_years ? '' : ' empty'}`}>
                  {asset.useful_life_years ? `${asset.useful_life_years} years` : 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Residual value</span>
                <span className={`detail-item-value${asset.residual_value ? '' : ' empty'}`}>
                  {asset.residual_value ? fmtMoney(asset.residual_value) : 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Custodian</span>
                <span className={`detail-item-value${asset.custodian ? '' : ' empty'}`}>
                  {asset.custodian?.user.full_name || 'Not set'}
                </span>
              </div>
            </>
          )}
          {asset.disposed_at && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">{isWrittenOff ? 'Written off on' : 'Disposed on'}</span>
                <span className="detail-item-value">{asset.disposed_at}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Disposal proceeds</span>
                <span className="detail-item-value">{fmtMoney(asset.disposal_proceeds ?? 0)}</span>
              </div>
            </>
          )}
          {asset.impaired_at && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Impaired on</span>
                <span className="detail-item-value">{asset.impaired_at}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Impairment loss</span>
                <span className="detail-item-value">{fmtMoney(asset.impairment_loss_amount ?? 0)}</span>
              </div>
            </>
          )}
          {asset.revalued_at && (
            <>
              <div className="detail-item">
                <span className="detail-item-label">Last revalued</span>
                <span className="detail-item-value">{asset.revalued_at}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Revalued amount</span>
                <span className="detail-item-value">{fmtMoney(asset.revalued_amount ?? 0)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Revaluation surplus balance</span>
                <span className="detail-item-value">{fmtMoney(asset.revaluation_surplus_balance ?? 0)}</span>
              </div>
            </>
          )}
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {asset.notes ? (
            <p className="detail-notes">{asset.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      {asset.revaluations && asset.revaluations.length > 0 && (
        <div className="card">
          <h2>Revaluation History</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="right">Previous Carrying Value</th>
                  <th className="right">Fair Value</th>
                  <th className="right">Surplus Movement</th>
                  <th className="right">P&amp;L Movement</th>
                  <th>Reason</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {asset.revaluations.map((r) => (
                  <tr key={r.id}>
                    <td className="muted">{r.revalued_at}</td>
                    <td className="right">{fmtMoney(r.previous_carrying_value)}</td>
                    <td className="right">{fmtMoney(r.fair_value)}</td>
                    <td className="right">{fmtMoney(r.surplus_movement)}</td>
                    <td className="right">{fmtMoney(r.pl_movement)}</td>
                    <td>{r.reason}</td>
                    <td className="muted">{r.created_by?.full_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {asset.disposal_requests.filter((r) => r.status !== 'pending').length > 0 && (
        <div className="card">
          <h2>Disposal Request History</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Requested by</th>
                  <th className="right">Proceeds</th>
                  <th>Outcome</th>
                  <th>Decided by</th>
                </tr>
              </thead>
              <tbody>
                {asset.disposal_requests
                  .filter((r) => r.status !== 'pending')
                  .map((r) => (
                    <tr key={r.id}>
                      <td className="muted">{r.disposed_at}</td>
                      <td>{r.requested_by.full_name}</td>
                      <td className="right">{fmtMoney(r.disposal_proceeds)}</td>
                      <td>
                        <span className={`badge ${r.status === 'approved' ? 'badge-active' : 'badge-urgent'}`}>
                          {r.status === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                        {r.decision_comment && <div className="muted">{r.decision_comment}</div>}
                      </td>
                      <td className="muted">{r.decided_by?.full_name ?? '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="changelog-release-header">
          <h2>Documents</h2>
        </div>
        {canUploadDocument && (
          <form onSubmit={uploadDocument} className="form-grid">
            <label>
              Category *
              <select required value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                <option value="">Select…</option>
                {listOptions(lists, 'asset_document_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              File *
              <input type="file" name="file" required accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
            </label>
            <div className="form-actions">
              <button className="btn btn-primary">Upload</button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {asset.documents.length > 0 ? (
                asset.documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>{labelFor(lists, 'asset_document_type', doc.category)}</td>
                    <td>{doc.original_name}</td>
                    <td className="right">
                      <IconButton
                        icon={IconDownload}
                        label="Download"
                        onClick={() => downloadFile(api, `/asset-documents/${doc.id}/download`, doc.original_name)}
                      />
                      {canDeleteDocument && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeDocument(doc)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted center">
                    No documents uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {requestingDisposal && (
        <Modal title="Request disposal" onClose={() => setRequestingDisposal(false)}>
          <form onSubmit={submitDisposalRequest} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">
              A different Resources Administrator will be emailed to approve this before the asset is actually
              marked disposed.
            </p>
            <label className="span-2">
              Disposed on *
              <input
                type="date"
                value={disposalRequestForm.disposed_at}
                onChange={(e) => setDisposalRequestForm({ ...disposalRequestForm, disposed_at: e.target.value })}
                required
              />
              {errors.disposed_at && <small className="error">{errors.disposed_at[0]}</small>}
            </label>
            <label className="span-2">
              Disposal proceeds ({settings.currency}) *
              <input
                type="number"
                step="0.01"
                min="0"
                value={disposalRequestForm.disposal_proceeds}
                onChange={(e) => setDisposalRequestForm({ ...disposalRequestForm, disposal_proceeds: e.target.value })}
                required
              />
              {errors.disposal_proceeds && <small className="error">{errors.disposal_proceeds[0]}</small>}
            </label>
            <label className="span-2">
              Reason *
              <textarea
                rows={2}
                value={disposalRequestForm.disposal_reason}
                onChange={(e) => setDisposalRequestForm({ ...disposalRequestForm, disposal_reason: e.target.value })}
                required
              />
              {errors.disposal_reason && <small className="error">{errors.disposal_reason[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRequestingDisposal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Submit Request</button>
            </div>
          </form>
        </Modal>
      )}

      {rejectingRequest && (
        <Modal title="Reject disposal request" onClose={() => setRejectingRequest(null)}>
          <form onSubmit={submitRejectDisposal} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Reason *
              <textarea rows={3} value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} required />
              {errors.decision_comment && <small className="error">{errors.decision_comment[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRejectingRequest(null)}>
                Cancel
              </button>
              <button className="btn btn-danger">Reject</button>
            </div>
          </form>
        </Modal>
      )}

      {markingImpaired && (
        <Modal title="Mark asset impaired" onClose={() => setMarkingImpaired(false)}>
          <form onSubmit={submitMarkImpaired} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Impaired on *
              <input
                type="date"
                value={impairForm.impaired_at}
                onChange={(e) => setImpairForm({ ...impairForm, impaired_at: e.target.value })}
                required
              />
              {errors.impaired_at && <small className="error">{errors.impaired_at[0]}</small>}
            </label>
            <label className="span-2">
              Impairment loss ({settings.currency}) *
              <input
                type="number"
                step="0.01"
                min="0"
                value={impairForm.impairment_loss_amount}
                onChange={(e) => setImpairForm({ ...impairForm, impairment_loss_amount: e.target.value })}
                required
              />
              {errors.impairment_loss_amount && <small className="error">{errors.impairment_loss_amount[0]}</small>}
            </label>
            <label className="span-2">
              Reason *
              <textarea
                rows={2}
                value={impairForm.impairment_reason}
                onChange={(e) => setImpairForm({ ...impairForm, impairment_reason: e.target.value })}
                required
              />
              {errors.impairment_reason && <small className="error">{errors.impairment_reason[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setMarkingImpaired(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {revaluing && (
        <Modal title="Revalue asset" onClose={() => setRevaluing(false)}>
          {revaluePreview ? (
            <div className="form-grid">
              <p className="muted span-2">Previous carrying value: {fmtMoney(revaluePreview.previous_carrying_value)}</p>
              <p className="muted span-2">New fair value: {fmtMoney(revaluePreview.fair_value)}</p>
              {revaluePreview.surplus_movement !== 0 && (
                <p className="span-2">
                  <strong>
                    Revaluation surplus {revaluePreview.surplus_movement >= 0 ? 'increase' : 'decrease'}:{' '}
                    {fmtMoney(Math.abs(revaluePreview.surplus_movement))}
                  </strong>
                </p>
              )}
              {revaluePreview.pl_movement !== 0 && (
                <p className="span-2">
                  <strong>P&amp;L loss (surplus exhausted): {fmtMoney(Math.abs(revaluePreview.pl_movement))}</strong>
                </p>
              )}
              <p className="muted span-2">
                Post the surplus movement to account 3210 — Asset Revaluation Reserve — and any P&amp;L loss to
                Impairment/Revaluation Loss, via a Journal Entry.
              </p>
              <div className="form-actions span-2">
                <button type="button" className="btn btn-primary" onClick={() => setRevaluing(false)}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitRevalue} className="form-grid">
              {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
              <label className="span-2">
                Revalued on *
                <input
                  type="date"
                  value={revalueForm.revalued_at}
                  onChange={(e) => setRevalueForm({ ...revalueForm, revalued_at: e.target.value })}
                  required
                />
                {errors.revalued_at && <small className="error">{errors.revalued_at[0]}</small>}
              </label>
              <label className="span-2">
                Fair value ({settings.currency}) *
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={revalueForm.fair_value}
                  onChange={(e) => setRevalueForm({ ...revalueForm, fair_value: e.target.value })}
                  required
                />
                {errors.fair_value && <small className="error">{errors.fair_value[0]}</small>}
              </label>
              <label className="span-2">
                Reason *
                <textarea
                  rows={2}
                  value={revalueForm.reason}
                  onChange={(e) => setRevalueForm({ ...revalueForm, reason: e.target.value })}
                  required
                />
                {errors.reason && <small className="error">{errors.reason[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button type="button" className="btn btn-ghost" onClick={() => setRevaluing(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary">Revalue</button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </>
  );
}
